import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Button, LinkButton, Machine } from '../../ui';
import { PlayFrame } from './PlayFrame';
import { Hud } from './Hud';
import { MissionBrief } from './MissionBrief';
import { LearningPractice, ReviewOffer } from './LearningPractice';
import { practiceLesson } from '../../lib/learning-practice';
import { GitGraph } from './GitGraph';
import { CommitDetail } from './CommitDetail';
import { Reflog } from './Reflog';
import { WorkPane } from './WorkPane';
import { HintLadder } from './HintLadder';
import { AskMentor } from './AskMentor';
import { Iris } from './Iris';
import { IrisCompanion } from './IrisCompanion';
import { Unstuck } from './Unstuck';
import { ClearOverlay } from './ClearOverlay';
import { SandboxRail } from './SandboxRail';
import { CheckpointStatus } from './CheckpointStatus';
import { Warmup } from '../warmup/Warmup';
import { useSession } from '../../state/useSession';
import { useCheckpoint } from '../../state/useCheckpoint';
import { useStuck } from '../../state/useStuck';
import { useIris } from '../../state/useIris';
import { useScriptedIris } from '../../state/useScriptedIris';
import { ScriptedIris } from './ScriptedIris';
import { gradeCounts } from '../../lib/grade';
import { useSpotHelp } from '../../state/useSpotHelp';
import { useStrings } from '../../state/useLocale';
import { md } from '../../lib/md';
import { irisCheckSummary, pendingStep } from '../../lib/iris';
import { irisBrief, irisPanel } from '@engineer-os/engine';
import { api } from '../../api';
import { registerLevel, reportCheck } from '../../lib/agent-bridge';
import { AgentStrip } from './AgentStrip';
import { AgentRail } from './AgentRail';
import { AGENT_FIRST } from '../../lib/agent-first';
import { sound } from '../../lib/sound';
import { track } from '../../lib/events';
import { openSignIn, signInUrl } from '../../lib/account';
import { currentObjective, setObjective } from '../../lib/presence';
import { markWarmupSeen, warmupSeen } from '../../lib/warmup';
import { openerForWorld, openerSeen, type OpenerCard } from '../../lib/openers';
import { onward } from '../../lib/entitlement';
import { LocalCheckStatus } from './LocalCheckStatus';
import {
  certificateNews, loadCertificates, refreshCertificates, type CertificateNews, type Certificates,
} from '../../lib/certificates';
import { onLevelPhase, type LevelProgress } from '../../engine/runtime';
import type { ToastFn } from '../../App';
import type { ClearResult } from '../contracts';
import type { GradeResult, PlausibilityFlag, PublicMission } from '../../types';
import { FREE_HINTS, levelProgress, struggledWith, tagPrefixes, totals } from '../../progress';
import { avatarProgression } from '../../lib/avatar';
import type { ClearEffort, Progress } from '../../progress';
import { COURSE_HREF } from '../../lib/landing';
import { usesGit } from '../../lib/uses-git';
import { meterOpen, meterPass } from '../../lib/efficiency';
import { CostPracticeGuide } from './CostPractice';
import { PreviewResults } from '../home/PreviewResults';
import { OptimizationJourney } from './OptimizationJourney';
import { isOptimizationMission } from '../../lib/optimization';

/**
 * The workstation.
 *
 * Owns the session, decides when a clear is worth celebrating, and wires the
 * keyboard. Everything visible is a leaf component; this file is composition
 * and policy only.
 *
 * The left rail is deliberately one column of one narrative: what the ticket
 * asks, what has to become true, and help if you want it. Nothing else on the
 * screen repeats any of it.
 */

/**
 * Commands in a row that ended non-zero before the level screen will pay to
 * ask which named wrong state the workspace is in.
 *
 * Three, and it is a lower bar than the stuck detector's own error streak on
 * purpose: this never shows the learner anything on its own. It only gives the
 * detector something to offer *if* it was already about to offer something, so
 * being early costs one probe and being late costs the offer entirely.
 */
const STUCK_COMMANDS = 3;

export interface PlayProps {
  userId?: string | null;
  mission: PublicMission;
  missions: PublicMission[];
  /**
   * The record, for one question: what this level opens.
   *
   * The rail used to answer that from the catalogue alone, because array
   * adjacency needs no record to read. Prerequisites do — whether the level
   * ahead is open depends on what is behind the learner, not only on what
   * they have bought — so the record comes down with the catalogue now
   * (App.tsx, `onward`). Nothing else on this screen reads it: the session
   * owns the grading and progress is written through `onClear`.
   */
  progress: Progress;
  /**
   * The chapter introductions, so that a clear which hands over to a new
   * world can offer its opener before the next level rather than after it.
   * Empty until the index has loaded, and empty is simply "no offer".
   */
  openers: OpenerCard[];
  /**
   * Play this level with the grading switched off: no objectives, no Check,
   * no hints, no XP, no attempt recorded and no clear. The workspace is the
   * level's own, seeded exactly as it always is.
   *
   * It exists because the only real terminal in this product was inside a
   * graded level, and the warm-up's try-prompts are authored rather than run
   * (TryPrompt.tsx). A learner who had just read a chapter opener and wanted
   * to type `git status` somewhere harmless had nowhere to do it.
   */
  sandbox?: boolean;
  /** `flags` is what the grader could not vouch for on a pass; empty or absent means the clear looked earned. */
  onClear(mission: PublicMission, hintsUsed: number, flags?: PlausibilityFlag[], effort?: ClearEffort): ClearResult;
  onAttempt(missionId: string): void;
  /** The learner left this level behind without clearing it. Earns nothing; opens the next one. */
  onSkip(missionId: string): void;
  notify: ToastFn;
}

export function Play({ mission, missions, progress, openers, sandbox = false, userId = null, onClear, onAttempt, onSkip, notify }: PlayProps) {
  const strings = useStrings();
  const s = strings.play;
  /**
   * Agent-first (lib/agent-first.ts): the rail offers the learner's own agent,
   * and Check my work grades the level either way. It used to wait for a
   * connected agent; since 2026-10-31 (owner) somebody who did the task
   * themselves is graded too, and the meter simply has no agent run to show.
   */
  const agentRail = AGENT_FIRST && !sandbox;
  const session = useSession(mission.id);
  const [selected, setSelected] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<ClearResult | null>(null);
  const [practiceRequest, setPracticeRequest] = useState(0);
  const [optimizationComplete, setOptimizationComplete] = useState(false);
  const [optimizationSkipped, setOptimizationSkipped] = useState(false);
  useEffect(() => { setOptimizationComplete(false); setOptimizationSkipped(false); }, [mission.id, userId]);
  /**
   * What this learner keeps losing time to, among the things this level is
   * about — a mastery-tag prefix, or null when none of them has earned the
   * right to be mentioned (progress.ts, `struggledWith`).
   *
   * The threshold is deliberately not this file's to pick. It fires at a score
   * of four across at least two levels, so that one bad hour does not tell
   * everybody they are struggling, and the two readers below ask the same
   * question the same way — a warm-up that opened on a different definition
   * from the one the debrief names the earlier level on would be two products
   * disagreeing about this learner on one screen.
   */
  const struggle = struggledWith(progress, mission.mastery_tags);
  /**
   * Offered once per level per browser, and only where a level authored one.
   * Play is keyed on the mission, so this is read fresh for each level.
   *
   * The exception is a level whose subject this learner has repeatedly come
   * off worst against (docs/mentor-plan.md §3.8): the warm-up opens by default
   * on those, seen or not. It is the tools for the level, and somebody who has
   * lost two levels to this area is exactly who they were written for — the
   * cost of being wrong is one click on a panel that closes.
   */
  const [warmupOpen, setWarmupOpen] = useState(
    () => !AGENT_FIRST && mission.tutorial !== undefined && (!warmupSeen(mission.id) || struggle !== null),
  );
  // The overlay stays mounted so it can animate out. While it is leaving,
  // `overlay` is already null, so the panel reads its contents from here.
  const lastClear = useRef<ClearResult | null>(null);
  // A clear is celebrated once per visit.
  const celebrated = useRef(false);
  // A checkpoint pass is written into the record once per visit, whichever of
  // the two routes to it got there first: a Check, or the account's record.
  const recordedPass = useRef(false);
  /**
   * The three refs that decide when the level screen pays for a situation
   * match (api.ts, `situation`).
   *
   * Matching runs every recognizer the level authored, in author order, until
   * one hits. On a level with seven of them that is seven round trips into an
   * emulator, so it is not something to do on every check — a learner whose
   * checks are failing while they work through the objectives one at a time is
   * making progress, and making them wait for it would be charging the whole
   * course for the few who are stuck.
   *
   * So: only after a check that failed, and only once the same failure has
   * come back twice or the prompt has been refusing for a while. Both are
   * shapes of stuck that the grade alone does not show. `lastFailing` is the
   * failing set of the previous check, as one string, because comparing two
   * sets is the whole of what it is for; `probing` stops a second match being
   * started while one is in the emulator.
   */
  const lastFailing = useRef<string | null>(null);
  const failedCommands = useRef(0);
  const probing = useRef(false);

  /**
   * Which part of opening this level is happening now, or null before the
   * first phase arrives and after the level is open.
   *
   * Cleared on the way in rather than on the way out: the screen is not
   * remounted between levels (docs/browser.md, decision 5), and the phase the
   * last level finished on would otherwise be the first thing drawn for this
   * one. The mission id on each report is what makes that safe — a phase
   * replayed for the level before this one is ignored rather than drawn.
   */
  const [opening, setOpening] = useState<LevelProgress | null>(null);
  useEffect(() => {
    setOpening(null);
    return onLevelPhase((p) => {
      if (p.missionId === mission.id) setOpening(p);
    });
  }, [mission.id]);

  const live = session.mission ?? mission;
  /**
   * A checkpoint is graded away from this tab, against a repository rebuilt
   * for this account alone (state/useCheckpoint.ts); every other level by the
   * session. "Check my work" is the same button for both, so from here down
   * there is one grade on screen and one `checking`, whichever produced them.
   */
  const checkpoint = live.graded === 'server';
  const assessment = useCheckpoint(live, session.session?.id ?? null, userId, checkpoint && !sandbox);
  const grade = checkpoint ? assessment.verdict?.result ?? null : session.grade;
  const checking = session.checking || assessment.checking;
  const levelCleared = Boolean(grade?.passed || levelProgress(progress, live.id).completed);
  /** The course's first level, where the screen holds only what the note needs. */
  const firstLevel = live.track === 'general' && live.level === 0;
  const reflection = checkpoint ? assessment.reflection : session.reflection;
  /**
   * The certificates as they stood before this checkpoint was passed, and
   * what the pass did to them (lib/certificates.ts, `certificateNews`).
   *
   * Read on the way in rather than on the pass, because "newly issued" is a
   * comparison and the pass is the moment the answer changes. The page keeps
   * one copy, so a learner who came from the map costs no second request.
   */
  const certificatesBefore = useRef<Certificates | null>(null);
  const [certificate, setCertificate] = useState<CertificateNews | null>(null);
  useEffect(() => {
    if (!checkpoint || sandbox || !userId) return;
    let alive = true;
    void loadCertificates(userId).then((value) => {
      if (alive && value) certificatesBefore.current = value;
    });
    return () => {
      alive = false;
    };
  }, [checkpoint, sandbox, userId]);
  /**
   * Which Iris this level gets (docs/iris.md, "Modes"). The engine decides
   * (`irisPanel`): her designed steps where the level has them, and
   * otherwise `none`, which is no Iris box at all and nothing saying so —
   * the hint ladder is the help there (owner, 2026-10-18). The `agent` and
   * `teach` branches below stay for the day `irisPanel` returns them again.
   */
  const iris = sandbox || AGENT_FIRST ? 'none' : irisPanel({
    track: live.track,
    level: live.level,
    irisBudget: live.irisBudget,
    scripted: Boolean(live.irisScript?.length),
  });
  const hintsUsed = session.hints.length;
  // Help that offers itself. It watches checks, command exit statuses and
  // silence; what it never sees is what was typed (state/useStuck.ts). The
  // warm-up holds it, and a learner who has not started is offered Iris's
  // first step where the level scripts her.
  const stuck = useStuck(mission.id, hintsUsed, live.hint_count, grade?.passed ?? false, {
    paused: warmupOpen,
    firstStep: iris === 'scripted' ? 'iris' : 'hint',
  });
  /** Bumped to open Iris from a nudge (IrisCompanion `summon`). */
  const [irisSummon, setIrisSummon] = useState(0);
  // Rungs that were never charged for: the standing free first one, plus
  // every rung the app pushed at somebody who was already stuck.
  //
  // Both are the same argument. Stars are charged for help the learner went
  // looking for, and neither an offer nor a question is that. Rung 1 is a
  // question by contract (docs/missions.md), and charging a star for a
  // question priced *asking* rather than *being told* — which is exactly the
  // hesitation that kept learners from asking at all. The mastery signal
  // survives intact: somebody who reads the answer still pays for it.
  /**
   * Rungs this level has decided not to charge for, because the workspace is
   * in a state that already demonstrates what they would say
   * (docs/mentor-plan.md §3.6).
   *
   * An allowance and not a grant: nothing here opens a rung. It moves the
   * price of the next few to nothing and leaves the decision where it was —
   * a learner who clears the level without opening any of them keeps all
   * three stars, exactly as they would have.
   *
   * Sparingly authored: thirteen situations across the course carry one, each
   * a judgement that a learner who has got the tree into *this* particular
   * mess has plainly already done what the first rungs describe. The highest
   * of the ones recognized wins, because they are statements about the same
   * ladder and the strongest is the one that is true.
   */
  const freeThrough = (live.situations ?? []).reduce(
    (best, sit) =>
      sit.free_through && session.situationsSeen.includes(sit.id) ? Math.max(best, sit.free_through) : best,
    0,
  );
  /*
   * A floor, not a sum, and this is the one arithmetic choice here worth
   * stating. "Rungs 1 through n cost nothing" is a statement about which
   * rungs are free; `stuck.freeHints` counts rungs the app pushed, which are
   * usually among the same first few. Adding them would charge three stars
   * for five rungs read. Taking the larger is the honest reading of both and
   * cannot make help the learner went looking for free.
   */
  const freeHints = Math.max(FREE_HINTS + stuck.freeHints, freeThrough);
  const paidHints = Math.max(0, hintsUsed - freeHints);
  /**
   * The earlier level this one is about to remind the learner of, or null.
   *
   * `struggledWith` answers with an *area* — a mastery-tag prefix — because
   * that is the grain the record keeps: about forty keys of four small
   * integers, and deliberately not a list of level ids (a record that named
   * every level a learner found hard would be a much larger record and a much
   * longer sentence in a privacy policy). So the level is found here, from the
   * catalogue and the record, which both already have to be on this screen.
   *
   * Which level, among the cleared ones sharing that area: the one that
   * actually went worst, by rungs bought plus checks failed. That is the level
   * the learner would name themselves if asked, and naming a different one
   * would make the sentence read as a guess.
   */
  /** When a level was first cleared, for the spaced recall cases (lib/learning-practice.ts). */
  const clearedAt = (id: string) => levelProgress(progress, id).completedAt;
  const recall = (() => {
    if (!struggle) return null;
    let worst: { level: number; title: string } | null = null;
    let worstScore = 0;
    for (const m of missions) {
      if (m.id === mission.id) continue;
      const record = levelProgress(progress, m.id);
      if (!record.completed || !tagPrefixes(m.mastery_tags).includes(struggle)) continue;
      const score = record.hintsUsed + record.attempts;
      if (score > worstScore) {
        worst = { level: m.level, title: m.title };
        worstScore = score;
      }
    }
    return worst;
  })();
  // What this level opens, and the one of them this rail has room for.
  //
  // `missions[idx + 1]` used to answer it: three assumptions wearing one
  // expression — that the catalogue is in path order, that the path is a
  // line, and that the level sitting next to this one is the level this one
  // leads to. All three hold while there is one track and all three stop
  // holding together, so the rail asks what this level opens rather than
  // inferring it from where it was filed (App.tsx, docs/curriculum.md §6).
  // The head of the set, by the rule stated there: forward, never a fork.
  //
  // `open` and not `barred`, which keeps the honesty the old expression had.
  // A locked stub has no level behind it, so a "Next · Level 20" button
  // pointing at one would take a learner who has just cleared level 19 --
  // the best moment they will ever be in -- straight to an error screen.
  // Falling back to null hands both callers their end-of-the-line state,
  // which is the honest answer: this is as far as this copy of the course
  // goes. The offer to go further belongs on the map, not on a button that
  // looks like every other Next.
  const next = onward(progress, missions, mission).open[0] ?? null;
  // Where leaving this level goes. The map, when the next level is one this
  // browser has not been given or there is no next one: the same honesty the
  // Next button keeps, rather than a button into an error screen.
  const skipHref = next ? `?mission=${encodeURIComponent(next.id)}` : '?';
  /**
   * The chapter the next level opens, when it opens one.
   *
   * Four conditions, and each one is a way of not interrupting somebody who
   * does not need it: there has to be a next level, it has to begin a
   * different world than the one just finished, that world has to have an
   * opener written, and this browser must not have been offered it already.
   * Anything else and the Next button is exactly what it has always been.
   *
   * Read at render rather than remembered, because `openerSeen` is storage
   * and the learner may have read the essay from the map thirty seconds ago.
   */
  const nextOpener = (() => {
    if (!next || next.track === 'checkpoint' || next.world === mission.world) return null;
    const card = openerForWorld(openers, next.world);
    if (!card || openerSeen(card.slug)) return null;
    return { slug: card.slug, title: card.title, world: card.world, minutes: card.minutes };
  })();

  /**
   * Whether the pass on screen is one the learner has actually claimed.
   *
   * Every grade on screen is now a Check's, which was not true while the free
   * mentor re-graded before speaking: a learner who solved the level and then
   * pressed for help had their objectives flipped by something nobody pressed,
   * and a clear declared off that grade would have had no plausibility
   * assessed against it. Nothing takes a grade uninvited any more, so this is
   * the grade itself — kept as a name rather than inlined, because what the
   * two readers below want is the *claim*, and the day something else grades
   * in the background this is where that distinction goes back.
   */
  const passedOnRecord = grade?.passed ?? false;

  useEffect(() => {
    if (passedOnRecord) celebrated.current = true;
  }, [passedOnRecord]);

  /**
   * Tell the chat which objective is being worked on, and stop telling it on
   * the way out.
   *
   * A level room's whole argument is that the people in it are stuck on the
   * same thing you are, and this is the number that says so. It goes to a
   * module rather than up through `App` because `App` re-renders the terminal
   * and the editor, and this changes on every grade (lib/presence.ts says the
   * rest).
   *
   * The cleanup is the half that matters. Leaving a level without clearing it
   * would otherwise leave the member list saying this learner is on objective
   * 2 of a level they walked away from an hour ago — and a room that is
   * confidently wrong about where people are is worse than one that says
   * nothing, because the first costs somebody a question.
   */
  useEffect(() => {
    setObjective(sandbox ? null : currentObjective(grade?.objectives));
    return () => setObjective(null);
  }, [sandbox, grade?.objectives]);

  const handleCheck = useCallback(async (): Promise<GradeResult | null> => {
    const wasPassed = passedOnRecord;
    const result = checkpoint ? await assessment.check() : await session.check();
    if (!result) return null;
    // While the tab is lent to an agent, the server's record of the run
    // (worker/agent-runs.ts) hears how each check went. Not lent, nothing is sent.
    const held = result.objectives.filter((o) => o.passed).length;
    const timedOut = result.objectives.some((o) => o.timedOut);
    reportCheck(mission.id, result.passed && !timedOut, `${result.passed && !timedOut ? 'Passed' : timedOut ? 'Not concluded' : 'Not yet'}. ${held} of ${result.objectives.length} objectives hold.`);
    // No conclusion means no evidence of a learner mistake or being stuck.
    if (result.objectives.some(o => o.timedOut)) return result;
    stuck.noteCheck(result);
    void session.refreshGit();
    // The meter's attempt on a level closes as passed (worker/ai-meter.ts);
    // a checkpoint's closes on the verifier's record instead.
    if (result.passed && !checkpoint && userId) meterPass(mission.id);
    if (result.passed) {
      if (!celebrated.current && !wasPassed) {
        celebrated.current = true;
        recordedPass.current = true;
        // How many named wrong states this attempt was actually found in. It
        // is the one struggle counter that is not already in the record's
        // hands, and without it the memory would record the level as hard only
        // by how much help was bought — which misses the learner who bought
        // none and went round three wrong turns to get there.
        //
        // It counts recognized states rather than named ones, which it did not
        // have to before: the panel that named them is gone, so what is left is
        // what the silent match found. That is a smaller number on the same
        // attempt — a match is only paid for once a learner has stopped
        // converging — and it is the honest one, because a state nobody was
        // ever told about is still a state they were in.
        const before = totals(progress, missions).cleared;
        const cleared = onClear(live, paidHints, result.flags, { situations: session.situationsSeen.length });
        const after = avatarProgression(before + (cleared.firstClear ? 1 : 0));
        cleared.avatarRewards = cleared.firstClear && after.latestLevel === before + 1 ? after.latest.length : 0;
        lastClear.current = cleared;
        setOverlay(cleared);
        // The stars, not the XP: three means nobody needed a hint, and a
        // level whose mean quietly sits at one is a level that is teaching
        // less than it looks like it is (docs/analytics.md).
        //
        // The track beside the level, here and on the four events below. A
        // level number is a position *within* a track and every track
        // numbers itself from zero, so "level 3" alone stops naming a level
        // the day a second one ships — the funnel would be averaging the
        // stars of the general track's third level together with the backend
        // track's (lib/events.ts, docs/analytics.md).
        track('level-clear', { level: mission.level, track: mission.track, value: cleared.stars });
        // The pass is on the account's record by the time the result is
        // back, and reading the certificates issues anything it completed
        // (docs/certificates.md, "When it is issued"). The overlay is already
        // open; the news joins it when it lands.
        if (checkpoint && userId) {
          const before = certificatesBefore.current;
          void refreshCertificates(userId).then((after) => {
            if (after) setCertificate(certificateNews(before, after, live.id));
          });
        }
      } else {
        notify(s.stillClear);
      }
    } else {
      onAttempt(mission.id);
      const outstanding = result.objectives.filter((o) => !o.passed && !o.skipped);
      const open = outstanding.length;

      /* Is this worth asking the workspace which mistake it is in?
       *
       * Two failed checks with the same answer, or a run of commands that all
       * came back non-zero. Either one says the learner has stopped converging,
       * which is the only state where naming the wrong state is worth seven
       * round trips into the emulator.
       *
       * Nothing is said about the answer and nothing on screen moves. What a
       * match buys is two things the learner never reads: the first rungs of
       * the ladder go free where the level authored a `free_through`, because
       * the state itself demonstrates what those rungs would have said; and
       * the id rides on the next question put to the model, where it is the
       * one fact about *this* tree the rules established. `null` is written
       * through as readily as an id — a tree that has moved out of a
       * recognized state has moved out of it, and a stale match is a
       * confidently wrong sentence in somebody's prompt. */
      const failing = outstanding.map((o) => o.id).sort().join(',');
      const repeated = lastFailing.current === failing;
      lastFailing.current = failing;
      // Never on a checkpoint: it authors no situations to match, and a match
      // exists to price hints and brief a mentor it does not have.
      if (!checkpoint && (repeated || failedCommands.current >= STUCK_COMMANDS) && !probing.current) {
        probing.current = true;
        failedCommands.current = 0;
        void session.situation().finally(() => {
          probing.current = false;
        });
      }

      // A failed check, with how much was still outstanding. This is the
      // confusion signal the roadmap asks for and the one number here that no
      // amount of looking at clears can produce: a level everybody passes on
      // the ninth attempt is indistinguishable from a good one until you
      // count the eight.
      track('level-check', { level: mission.level, track: mission.track, value: open });
      // A checkpoint refused at a gate has no objectives to count, and says
      // what to fix under them instead (CheckpointStatus.tsx).
      // Waiting on a prerequisite still means an objective is left to satisfy.
      // Count the visible objectives the same way the brief's verdict does.
      const { passed, total } = gradeCounts(live, result);
      if (total > passed) notify(s.objectivesToGo(total - passed));
    }
    return result;
  }, [session, assessment, checkpoint, live, paidHints, passedOnRecord, stuck, onClear, onAttempt, mission.id, mission.level, mission.track, notify, s, userId, progress, missions]);

  /**
   * A checkpoint the account passed that this browser never recorded — on
   * another device, or before "Check my work" could grade one — goes into the
   * record quietly, so the map shows what the account already holds. No
   * overlay and no analytics: nothing was cleared on this visit.
   */
  useEffect(() => {
    if (!checkpoint || !assessment.passed || recordedPass.current) return;
    recordedPass.current = true;
    if (!levelProgress(progress, live.id).completed) onClear(live, 0);
  }, [checkpoint, assessment.passed, progress, live, onClear]);

  const handleHint = useCallback(async () => {
    if (hintsUsed + 1 === live.hint_count && !window.confirm(s.confirmLastHint)) return null;
    const revealed = await session.askHint();
    // Only a rung that actually arrived. `askHint` answers null on a failure
    // and shows the learner an error, and counting that as help given would
    // put a hint against a level where nobody got one.
    //
    // The rung number is what makes this worth recording at all: "which levels
    // need help" and "how far down the ladder people go before it helps" are
    // different questions, and a level where everybody reaches rung five is one
    // whose earlier rungs are not working.
    if (revealed) track('hint', { level: mission.level, track: mission.track, value: revealed.length });
    return revealed;
  }, [hintsUsed, live.hint_count, session, s, mission.level, mission.track]);

  /**
   * What came of a turn at the online mentor, and which turn it was.
   *
   * The turn index is the value because the interesting question about this
   * feature is not how many people used it but how far a conversation gets: a
   * level where everybody asks once and leaves is a different level from one
   * where they ask four times and clear it.
   */
  const handleAskOutcome = useCallback((detail: string, turn: number) => {
    track('mentor-ask', { level: mission.level, track: mission.track, detail, value: turn });
  }, [mission.level, mission.track]);

  /**
   * Sign in, from the composer's signed-out line.
   *
   * A window rather than a redirect, and the fallback is the redirect, for the
   * reason `useAccount` gives: navigating away from this screen throws away a
   * running virtual machine and whatever the learner had done in it. Reached
   * through the module rather than through an account prop because this screen
   * is handed a `userId` and nothing else — which is all it needs, since the
   * one question it asks is whether there is anybody to spend an allowance
   * against.
   */
  const handleSignIn = useCallback(() => {
    if (!openSignIn()) window.location.assign(signInUrl(false));
  }, []);

  /**
   * Sign in to connect an agent, from the rail. The window also offers a guest
   * (worker/account.ts `createGuest`): on the free levels an agent needs an
   * account to hold its key, not an address (owner, 2026-10-31).
   */
  const handleAgentSignIn = useCallback(() => {
    if (!openSignIn({ guest: true })) window.location.assign(signInUrl(false, { guest: true }));
  }, []);

  /**
   * On the general levels where Iris is the live agent but the mentor still
   * teaches (up to level 12), "explain it to me" stays beside "do it"
   * (docs/agent-first-plan.md, decision 1).
   */
  const explainToo = iris === 'agent' && live.track === 'general' && live.level <= 7;

  /**
   * What the rail is showing, as of the last render, for Iris's `check` step.
   *
   * A level's Check returns its grade. A checkpoint's often returns nothing —
   * progress checks only, or a gate refused the work — and draws what it
   * found into state instead, so the summary is read from what was drawn.
   */
  const shown = useRef<{ mission: PublicMission; grade: GradeResult | null; notes: string[] }>({ mission: live, grade, notes: [] });
  shown.current = {
    mission: live,
    grade: checkpoint ? assessment.verdict?.grade ?? null : session.grade,
    notes: checkpoint
      ? [
          ...(assessment.verdict?.gates ?? []).map((gate) => strings.checkpoint.gates[gate]),
          ...(assessment.problem ? [strings.checkpoint[assessment.problem]] : []),
          ...(assessment.output ? [`Test output:\n${assessment.output}`] : []),
        ]
      : [],
  };

  const handleIrisCheck = useCallback(async (): Promise<string> => {
    const result = await handleCheck();
    // One task, so that what the Check set in state has rendered into
    // `shown` before it is read.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const now = shown.current;
    return irisCheckSummary(now.mission, result ?? now.grade, now.notes);
  }, [handleCheck]);

  /**
   * Bumped after each command Iris ran so workspace readers (the Editor tree
   * and pull requests) see the same files and state as the agent's terminal.
   */
  const [irisRuns, setIrisRuns] = useState(0);
  const handleIrisRan = useCallback(() => {
    void session.refreshGit();
    setIrisRuns((n) => n + 1);
    // Activity, for the stuck detector's clock, and not a command result: a
    // step Iris proposed that failed says nothing about whether the learner
    // is stuck.
    stuck.noteSave();
  }, [session, stuck]);

  const irisApi = useIris({
    mission: live,
    sessionId: session.session?.id ?? null,
    resetKey: session.reconnectKey,
    onRan: handleIrisRan,
    onCheck: handleIrisCheck,
    onSignIn: handleSignIn,
  });

  /**
   * The learner's own agent, when they lent it this tab (lib/agent-bridge.ts,
   * docs/agents.md). It acts through what Iris acts through, the editor's
   * save and Check my work, so it is graded and counted exactly as she is.
   * Registered once per level; the ref keeps every call on the latest state.
   */
  /**
   * This level opened in the tab: the meter opens its attempt, so an agent
   * the tab is lent to is counted to it (worker/ai-meter.ts). Signed in only;
   * fire and forget.
   */
  useEffect(() => {
    if (!sandbox && userId) meterOpen(live.id);
  }, [live.id, sandbox, userId]);

  const agentNow = useRef({ sessionId: session.session?.id ?? null, checkpoint, ran: handleIrisRan, check: handleIrisCheck });
  agentNow.current = { sessionId: session.session?.id ?? null, checkpoint, ran: handleIrisRan, check: handleIrisCheck };
  useEffect(() => {
    if (sandbox) return undefined;
    const need = () => {
      const id = agentNow.current.sessionId;
      if (!id) throw new Error('The level is still opening.');
      return id;
    };
    return registerLevel({
      missionId: live.id,
      ready: () => agentNow.current.sessionId !== null,
      status: () => {
        const now = shown.current;
        return {
          level: {
            id: now.mission.id,
            title: now.mission.title,
            kind: agentNow.current.checkpoint ? 'checkpoint' : 'level',
            folder: now.mission.cwd,
            brief: irisBrief(now.mission),
          },
          lastCheck: now.grade || now.notes.length > 0 ? irisCheckSummary(now.mission, now.grade, now.notes) : null,
          // Already passed in this workspace: opening it again must not start
          // a metered run on solved work (worker/mcp.ts, open_level).
          ...(now.grade?.passed ? { solved: true, hint: 'Already solved in this tab. The learner presses Replay to reset it for another measured run.' } : {}),
        };
      },
      run: async (command) => {
        const result = await api.irisRun(need(), command);
        agentNow.current.ran();
        return result;
      },
      listFiles: (path) => api.listFiles(need(), path),
      readFile: (path) => api.readFile(need(), path),
      writeFile: async (path, content) => {
        const result = await api.writeFile(need(), path, content);
        agentNow.current.ran();
        return result;
      },
      check: () => agentNow.current.check(),
    });
  }, [live.id, sandbox]);

  const scriptedIris = useScriptedIris({
    mission: live,
    sessionId: session.session?.id ?? null,
    resetKey: session.reconnectKey,
    onRan: handleIrisRan,
    onCheck: handleIrisCheck,
  });

  /**
   * The learner took the offer. `accept` is called first so the hint that
   * follows is counted as free even if the request is slow, and the rung it
   * opens is the next one — the offer is worth exactly one rung, wherever the
   * learner had got to.
   */
  const handleNudge = useCallback(async () => {
    const action = stuck.nudge?.action;
    stuck.accept();
    if (action === 'hint') await session.askHint();
    if (action === 'check') await handleCheck();
    if (action === 'iris') {
      setIrisSummon((n) => n + 1);
      if (!scriptedIris.started) scriptedIris.start();
    }
    // 'skip' is the third, and it is a link rather than a call: the exit is a
    // real href so that the browser's own back button knows where the learner
    // went.
  }, [stuck, session, handleCheck, scriptedIris]);

  useSpotHelp({ session, mission:live, mode:iris==='scripted'?'none':iris, sandbox, userId, hint:handleHint, check:handleCheck, signIn:handleSignIn });

  /**
   * Leaving a level behind. The click is on a real link, so cancelling the
   * confirmation has to stop the navigation the App's delegated listener
   * would otherwise do — it honours `defaultPrevented`.
   */
  /**
   * How the command that just finished ended.
   *
   * Passed straight through to the detector, which is the component that
   * actually decides anything about it; the count kept here is a second,
   * coarser reading of the same signal, and it exists only to answer "has the
   * prompt been refusing for a while" at the moment a check fails. A command
   * that worked resets it, because a run is a run.
   */
  const handleCommandResult = useCallback(
    (ok?: boolean) => {
      stuck.noteCommand(ok);
      if (ok === false) failedCommands.current += 1;
      else if (ok === true) failedCommands.current = 0;
    },
    [stuck],
  );

  const handleSkip = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (!window.confirm(s.confirmSkip)) {
        event.preventDefault();
        return;
      }
      stuck.accept();
      // Giving up on a level, which is the strongest drop-off signal the
      // product has. Somebody who simply stops coming back is a gap in the
      // data; somebody who presses this has named the level that beat them.
      track('level-skip', { level: mission.level, track: mission.track });
      onSkip(mission.id);
    },
    [stuck, onSkip, mission.id, mission.level, mission.track, s],
  );

  /** `ask` is false only from the clear's Run it again, where the button already says what it does. */
  const handleReset = useCallback(async (ask = true) => {
    if (ask && !window.confirm(s.confirmReset)) return false;
    assessment.forget();
    if (await session.reset()) {
      // A successful reset after a pass begins a new normal-level cost
      // attempt. While already open the meter keeps the same attempt, so
      // resetting after a failure cannot erase what the agent spent.
      if (!sandbox && !checkpoint && userId) meterOpen(live.id);
      celebrated.current = false;
      setSelected(null);
      // The workspace this was all about has been rebuilt from the seed, so
      // the failure that kept coming back and the commands that kept refusing
      // were about a tree that no longer exists. `noteReset` clears the
      // detector's own copy, including the matched situation; these two are
      // this screen's.
      lastFailing.current = null;
      failedCommands.current = 0;
      stuck.noteReset();
      sound.reset();
      notify(s.workspaceReset);
      return true;
    }
    return false;
  }, [session, assessment, stuck, notify, s, sandbox, checkpoint, userId, live.id]);

  // Ctrl+Enter is the Check button's shortcut, so it is not bound in a
  // sandbox: the listener is never added rather than added and ignored, which
  // keeps the key free for the terminal underneath.
  useEffect(() => {
    if (sandbox) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!checking) void handleCheck();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sandbox, handleCheck, checking]);

  useEffect(() => {
    if (session.error) notify(session.error.message, 'error');
  }, [session.error, notify]);

  // Said once per open, and only when there is something to say: a level
  // that came back as it was left, or one that could not because it changed.
  useEffect(() => {
    if (session.resumed) notify(session.resumed.kind === 'restored' ? s.workRestored : s.workLevelChanged);
  }, [session.resumed, notify, s]);

  // Every route out of the warm-up comes through here, so every route out of
  // it counts as the offer having been made.
  const closeWarmup = useCallback(
    (reason: 'done' | 'skip') => {
      setWarmupOpen(false);
      markWarmupSeen(mission.id);
      // Only the dismissals are recorded. The warm-up is free, optional and
      // costs no stars, so the question it has to answer for itself is
      // whether anybody wants it — and "read to the end" is already implied
      // by the level start that follows without one of these.
      if (reason === 'skip') track('warmup-skip', { level: mission.level, track: mission.track });
    },
    [mission.id, mission.level, mission.track],
  );

  // Built here, above the early returns, and rendered as the first child of
  // all three of them. The warm-up is at its most useful while the workspace
  // is still seeding: covering that dead time with the tools the level needs
  // is half the point of the feature, and a learner who reads two cards
  // during the boot has spent none of their own time on it.
  //
  // First child and keyed, both deliberately. When the session arrives this
  // component returns a different tree, and an overlay that moved position in
  // it would be unmounted and mounted again — throwing the learner back to
  // card one at the exact moment the workspace finished. It is `position:
  // fixed`, so being first costs nothing visually.
  const warmup = live.tutorial ? (
    <Warmup key="warmup" open={warmupOpen} mission={live} tutorial={live.tutorial} onClose={closeWarmup} />
  ) : null;

  // A level with no session and a failure behind it is not loading; it is
  // stuck. Now that the workspace is the Linux in this tab, "could not open
  // the level" is a real outcome — the machine may still be downloading, or a
  // command may have timed out — and without this the screen said "Preparing
  // your workspace." for ever behind a toast that had already faded.
  if (!session.session && session.error) {
    return (
      <>
        {warmup}
        <div className="boot-error">
          <span className="label">{s.levelLabel(mission.level)}</span>
          <h1 className="rail-title">{mission.title}</h1>
          <p className="lede">{s.couldNotOpen}</p>
          <p className="boot-actions">
            <Button variant="solid" onClick={session.retry}>
              {s.tryAgain}
            </Button>
            <LinkButton href={COURSE_HREF}>{s.allLevels}</LinkButton>
          </p>
          {/* The failure came off the wire in English. Isolated, so it does
              not drag the Arabic around it out of order. */}
          <p className="boot-detail">
            <Machine>{session.error.message}</Machine>
          </p>
        </div>
      </>
    );
  }

  if (session.loading || !session.session) {
    const here = opening ? opening.steps.indexOf(opening.phase) : -1;
    return (
      <>
        {warmup}
        <div className="boot-error">
          <span className="label">{s.levelLabel(mission.level)}</span>
          <h1 className="rail-title">{mission.title}</h1>
          <p className="note">{s.preparing}</p>
          {/* Only once a phase has arrived. A restored session reports none —
              there is nothing to seed — and it would be drawing a wait that
              is one read long. */}
          {opening && (
            <>
              {/* Indeterminate on purpose. The phases are steps rather than a
                  fraction: an extraction and a boot script are not the same
                  size, and a bar that filled in equal thirds would be a
                  measurement nobody made. */}
              <div className="boot-bar" role="progressbar" aria-label={s.opening.barLabel}>
                <div className="boot-bar-fill is-waiting" />
              </div>
              <ol className="boot-steps">
                {opening.steps.map((phase, i) => (
                  <li key={phase} className={`boot-step is-${i < here ? 'done' : i === here ? 'now' : 'todo'}`}>
                    <span className="boot-step-mark" aria-hidden="true" />
                    <span>{s.opening.steps[phase]}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      </>
    );
  }

  const sessionId = session.session.id;

  // What to say about a pass the grader could not vouch for. The recorded
  // clear is authoritative when this visit made one: progress adds the
  // level-sprint check the grader cannot see, and drops every flag on a
  // level already verified. When no clear was recorded this visit (a check
  // after a pass) the grade's own flags are all there is.
  const shownFlags = lastClear.current ? lastClear.current.flags : (grade?.flags ?? []);

  return (
    <>
      {warmup}

      <PlayFrame
        onIris={!checkpoint && !sandbox && !AGENT_FIRST ? () => setIrisSummon((n) => n + 1) : undefined}
        hud={
          <Hud
            mission={live}
            git={session.git}
            checking={checking}
            sandbox={sandbox}
            onCheck={() => void handleCheck()}
            onReset={() => void handleReset()}
            onWarmup={live.tutorial && !AGENT_FIRST ? () => setWarmupOpen(true) : undefined}
          />
        }
        brief={
          sandbox ? (
            // Nothing from the graded rail survives here. The ticket describes
            // a task that is not being asked, the objectives grade nothing,
            // and the hint ladder answers a question nobody put. What is left
            // is what a scratch repository actually needs: what this is, what
            // you might type, and the way back out.
            <SandboxRail
              mission={live}
              openerSlug={openerForWorld(openers, live.world)?.slug ?? null}
              realHref={`?mission=${encodeURIComponent(live.id)}`}
            />
          ) : (
          <>
            {/* The nudge, pinned to the top of the rail rather than in the
                Help section it belongs to: on a 1280×720 screen that section
                starts about a thousand pixels below the fold (measured on
                level 0, 2026-10-10), so a stuck learner who is not scrolling
                the rail — the only kind it is for — never saw it. A
                checkpoint has none (docs/checkpoints.md, rule 2). */}
            {!checkpoint && !agentRail && (
              <div className="nudge-dock">
                <Unstuck
                  nudge={stuck.nudge}
                  skipHref={skipHref}
                  onAccept={() => void handleNudge()}
                  onSkip={handleSkip}
                  onDismiss={stuck.dismiss}
                />
              </div>
            )}
            {/* On a checkpoint refused at a gate there is nothing to draw in
                the list, and the status under it says what to fix. */}
            <MissionBrief mission={live} grade={checkpoint ? assessment.verdict?.grade ?? null : session.grade} />
            {!checkpoint && !agentRail && <CostPracticeGuide key={`${userId}:${live.id}`} signedIn={userId !== null} />}
            {live.id === 'l17-who-broke-it' && levelCleared && <PreviewResults key={userId ?? 'guest'} missions={missions} progress={progress} userId={userId} />}
            {/* Not before level 0 is cleared: a quiz about `>` on the very
                first screen, before the first command, is one more thing
                between a newcomer and the note they were asked to read. */}
            {!agentRail && (!firstLevel || levelCleared) && <LearningPractice mission={live} userId={userId}
              cleared={levelCleared} requestTransfer={practiceRequest} clearedAt={clearedAt} />}
            {!agentRail && !firstLevel && <ReviewOffer key={`${userId ?? 'guest'}:${live.id}`} mission={live}
              missions={missions} userId={userId} clearedAt={clearedAt} />}
            {checkpoint && <CheckpointStatus check={assessment} />}
            {!checkpoint && <LocalCheckStatus checking={session.checking} grade={session.grade}
              onRetry={() => { void handleCheck(); }} />}

            {/* A pass the grader could not vouch for. One line, no colour, and
                nothing is withheld: the level is cleared, the next one is open.
                Replaying it plausibly is how the mark comes off. */}
            {agentRail && isOptimizationMission(live.id) && (
              <OptimizationJourney missionId={live.id} userId={userId} passed={grade?.passed === true}
                open={overlay === null} target="tab" onComplete={setOptimizationComplete} onContinue={() => setOptimizationSkipped(true)}
                onReplay={async () => { if (!await handleReset(false)) throw new Error('reset failed'); }} />
            )}
            {agentRail && !isOptimizationMission(live.id) ? (
              <AgentRail mission={live} userId={userId} checkpoint={checkpoint} passed={grade?.passed === true}
                onSignIn={handleAgentSignIn} onReplay={() => void handleReset()} />
            ) : userId && !sandbox && <AgentStrip />}

            {grade?.passed && shownFlags.length > 0 && (
              // The reasons go to the catalogue as a list, not as a string
              // already joined with '; ' — Arabic separates a list with '؛'.
              <p className="unverified">{s.unverified(shownFlags.map((f) => f.detail))}</p>
            )}

            {agentRail ? null : checkpoint ? (
              /* An assessment: no nudges, no mentor, no ladder
                 (docs/checkpoints.md, rule 2). The Worker refuses the mentor
                 on a checkpoint anyway, so the composer could only ever fail
                 here. What stays is the exit, and on the clock alone: with no
                 ladder, "the ladder is spent" is true on the first render and
                 would offer a way out before the brief had been read.

                 The one exception is Iris, on a checkpoint built so that
                 approving everything she proposes fails it (docs/iris.md).
                 What is assessed there is working with an agent, so she is
                 the whole of the help, and the rule is stated beside her
                 before she proposes anything. */
              <>
                {iris === 'agent' && (
                  <section className="block">
                    <IrisCompanion key={`${sessionId}:${session.reconnectKey}`} busy={irisApi.busy??(assessment.checking?'checking':null)}
                      reviewing={Boolean(pendingStep(irisApi.conversation))}
                      responseCount={irisApi.conversation.turns.filter(turn=>turn.role==='model').length}
                      feedback={assessment.verdict?.grade??null}>
                      <p className="note iris-rule">{strings.iris.rule}</p>
                      <Iris iris={irisApi} userId={userId} mission={live} />
                    </IrisCompanion>
                  </section>
                )}
                {/* Designed agents on a checkpoint (docs/designed-agents.md,
                    section E): the same panel as on a level, and the agents
                    are the whole of the help. Their careful path never passes
                    on its own; one step stays the learner's. */}
                {iris === 'scripted' && (
                  <section className="block">
                    <IrisCompanion key={`${sessionId}:${session.reconnectKey}`} busy={scriptedIris.busy??(assessment.checking?'checking':null)}
                      reviewing={Boolean(scriptedIris.beat&&!scriptedIris.busy&&(scriptedIris.beat.run!==undefined||scriptedIris.beat.check))}
                      responseCount={scriptedIris.log.filter(entry=>entry.kind==='step').length}
                      feedback={assessment.verdict?.grade??null}>
                      <p className="note iris-rule">{strings.iris.checkpointNote}</p>
                      <ScriptedIris iris={scriptedIris} />
                    </IrisCompanion>
                  </section>
                )}
                {stuck.mayBail && !grade?.passed && (
                  <section className="block">
                    <p className="bail">
                      {s.stillNotPassing}
                      <LinkButton variant="quiet" wide href={skipHref} onClick={handleSkip}>
                        {s.skipLevel}
                      </LinkButton>
                    </p>
                  </section>
                )}
              </>
            ) : (
            <section className="block">
              <h2 className="label">{s.help}</h2>
              {/* Three things, cheapest first, and the order is the
                  product's opinion about being stuck: the nudge offered
                  itself and costs nothing; the composer costs an allowance
                  and needs an account; the ladder costs stars.

                  There were four. The one that is gone sat between the nudge
                  and the composer and read the working tree for free, every
                  press, for ever — and it could only ever say what is true of
                  everybody on the level (docs/mentor.md). Nothing has taken
                  its slot, and nothing should: a panel is not owed a middle
                  rung, and a turn that comes back with no answer now says
                  which fact stopped it rather than being handed a sentence
                  that was not an answer to anything. */}
              {/* Iris takes the composer's slot where she works: the same
                  cost and the same account, and she proposes work instead
                  of answering. Her conversation is held by `useIris` above,
                  keyed on the session for the reason the mentor's is. */}
              {/* No designed steps here, no Iris box (`irisPanel`): the
                  ladder below is the whole of the help, and nothing says
                  that Iris is missing. */}
              {iris !== 'none' && (
                <IrisCompanion key={`${sessionId}:${session.reconnectKey}`} summon={irisSummon} busy={(iris === 'scripted' ? scriptedIris.busy : iris === 'agent' ? irisApi.busy : session.asking ? 'thinking' : null)??(session.checking?'checking':null)}
                  reviewing={iris==='scripted'?Boolean(scriptedIris.beat&&!scriptedIris.busy&&(scriptedIris.beat.run!==undefined||scriptedIris.beat.check)):iris==='agent'&&Boolean(pendingStep(irisApi.conversation))}
                  responseCount={iris==='scripted'?scriptedIris.log.filter(entry=>entry.kind==='step').length:iris==='agent'?irisApi.conversation.turns.filter(turn=>turn.role==='model').length:session.conversation.turns.filter(turn=>turn.role==='mentor').length}
                  feedback={session.grade}>
                {iris === 'scripted' ? (
                  <ScriptedIris iris={scriptedIris} />
                ) : iris === 'agent' ? (
                  <>
                    <p className="note iris-rule">{strings.iris.levelRule}</p>
                    <Iris iris={irisApi} userId={userId} mission={live} />
                    {explainToo && (
                      <details className="iris-learning iris-explain">
                        <summary>{strings.iris.explainInstead}</summary>
                        <AskMentor
                          mission={live}
                          userId={userId}
                          busy={session.asking}
                          conversation={session.conversation}
                          onAsk={(message) => session.askModel(message)}
                          onSignIn={handleSignIn}
                          onOutcome={handleAskOutcome}
                        />
                      </details>
                    )}
                  </>
                ) : (
                <AskMentor
                  mission={live}
                  userId={userId}
                  busy={session.asking}
                  // The transcript is the session's, not the panel's, because
                  // this screen is not remounted between levels and a reset has
                  // to clear it. Both are facts about the workspace, and the
                  // workspace is `useSession`'s.
                  conversation={session.conversation}
                  onAsk={(message) => session.askModel(message)}
                  onSignIn={handleSignIn}
                  onOutcome={handleAskOutcome}
                />
                )}
                </IrisCompanion>
              )}
              <HintLadder
                hints={session.hints}
                hintCount={live.hint_count}
                freeHints={freeHints}
                freeThrough={freeThrough}
                busy={session.hinting}
                onAsk={() => void handleHint()}
              />

              {/* The exit. Offered here as well as in a nudge, because a
                  learner who has read the answer and still cannot pass should
                  not have to wait to be asked whether they are stuck — and
                  offered on the clock as well as on the ladder, because
                  making someone read the answer to earn the right to move on
                  is a toll, not a lesson. */}
              {(stuck.mayBail || hintsUsed >= live.hint_count) && !grade?.passed && (
                <p className="bail">
                  {s.stillNotPassing}
                  <LinkButton variant="quiet" wide href={skipHref} onClick={handleSkip}>
                    {s.skipLevel}
                  </LinkButton>
                </p>
              )}
            </section>
            )}

            {reflection && (
              <section className="block">
                <h2 className="label">{s.whatJustHappened}</h2>
                <div className="prose" dangerouslySetInnerHTML={md(reflection)} />
                {/* The reflection is two paragraphs on purpose. Everything
                    this level used, and why, is the debrief. */}
                <LinkButton variant="quiet" wide href={`?debrief=${encodeURIComponent(live.id)}`}>
                  {s.readDebrief}
                </LinkButton>
                {/* When the next level opens a chapter, the chapter opens
                    first. Same rule as the clear overlay, so the rail and the
                    panel never disagree about what "next" means. */}
                {(!agentRail || !isOptimizationMission(live.id) || optimizationComplete || optimizationSkipped) && (next && nextOpener ? (
                  <>
                    <LinkButton variant="solid" wide href={`?opener=${encodeURIComponent(nextOpener.slug)}`}>
                      {s.nextWorld(nextOpener.world)}
                    </LinkButton>
                    <LinkButton variant="quiet" wide href={`?mission=${encodeURIComponent(next.id)}`}>
                      {s.straightToLevel(next.level)}
                    </LinkButton>
                  </>
                ) : (
                  next && (
                    <LinkButton variant="solid" wide href={`?mission=${encodeURIComponent(next.id)}`}>
                      {next.track === 'checkpoint' ? next.title : s.nextLevel(next.level)}
                    </LinkButton>
                  )
                ))}
              </section>
            )}
          </>
          )
        }
        graph={
          <GitGraph
            git={session.git}
            selected={selected}
            onSelect={setSelected}
            emptyMessage={live.expects_repo ? undefined : s.noRepoYet}
          />
        }
        detail={<CommitDetail git={session.git} hash={selected} />}
        reflog={<Reflog git={session.git} selected={selected} onSelect={setSelected} />}
        // The reflog is the subject of exactly one level and clutter on the
        // rest, so the level decides whether it starts open.
        reflogDefaultOpen={live.mastery_tags.some((t) => t.includes('reflog'))}
        // Until a repository exists there is no graph and no reflog to show,
        // so the terminal takes the room instead of a large empty panel; on a
        // level that never touches git the graph is not drawn at all.
        bare={!session.git.isRepo}
        noGraph={!session.git.isRepo && !usesGit(live)}
        work={
          <WorkPane
            sessionId={sessionId}
            cwd={live.cwd}
            mission={live}
            onGitState={session.setGit}
            onCommandResult={handleCommandResult}
            reconnectKey={session.reconnectKey}
            commandsElsewhere={irisRuns}
            onSaved={() => {
              stuck.noteSave();
              void session.refreshGit();
            }}
          />
        }
      />

      {lastClear.current && (
        <ClearOverlay
          open={overlay !== null}
          mission={live}
          result={overlay ?? lastClear.current}
          hintsUsed={paidHints}
          freeHints={Math.min(hintsUsed, freeHints)}
          reflection={reflection ?? live.brief}
          next={next}
          opener={nextOpener}
          // What the attempt cost, which is a fact about this attempt rather
          // than about the record, and so comes off the session — or, on a
          // checkpoint, off the hook that grades it, since the session never
          // sees a checkpoint's checks.
          checks={checkpoint ? assessment.checks : (session.session?.checks ?? 0)}
          certificate={checkpoint ? certificate : null}
          // The earlier level that went the same way, when the record says one
          // did. Null on every other clear, which is most of them.
          recall={recall}
          userId={userId}
          onClose={() => setOverlay(null)}
          onOptimizationComplete={setOptimizationComplete}
          onOptimizationContinue={() => setOptimizationSkipped(true)}
          // The second of the two runs (TwoRuns.tsx): the level from its seed, for a fresh agent.
          onReplay={agentRail && !checkpoint ? async () => {
            const reset = await handleReset(false);
            if (reset) setOverlay(null);
            else if (isOptimizationMission(live.id)) throw new Error('reset failed');
          } : undefined}
          onPractice={practiceLesson(live) ? () => {
            setOverlay(null);
            setPracticeRequest((value) => value + 1);
          } : undefined}
        />
      )}
    </>
  );
}
